import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemAvatar,
    Avatar,
    ListItemText,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    useTheme
} from "@mui/material";
import {
    Delete,
    Add,
    Edit,
    Category as CategoryIcon
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";
import { getImageFullUrl } from "../utils/common";
import FallbackImage from "../components/FallbackImage";
import ConfirmDialog from "../components/ConfirmDialog";

const CategoryManager = () => {
    const [categories, setCategories] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [mode, setMode] = useState("");
    const [formData, setFormData] = useState({
        id: "",
        name: "",
        parentId: "",
        iconFile: null,
        iconPreview: "",
        level: 0
    });

    // ✅ Fetch categories
    const fetchCategories = async () => {
        try {
            const res = await apiClient.get("/api/categories");
            setCategories(res?.data?.data || []);
        } catch (err) {
            toast.error("Failed to load categories");
        }
    };

    // ✅ Delete category
    const deleteCategory = async (id) => {
        try {
            await apiClient.delete(`/api/categories/${id}`);
            toast.success("Category deleted");
            fetchCategories();
        } catch (err) {
            toast.error("Failed to delete category");
        }
    };


    // ✅ Confirm dialog state
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deleteData, setDeleteData] = useState({});

    // ✅ Show confirm dialog before delete
    const requestDelete = (id) => {
        setDeleteData(id);
        setConfirmOpen(true);
    };

    // ✅ Open Add/Edit dialog
    const handleOpenDialog = (category = null, parent = null, level = 0) => {
        if (category) {
            // Editing
            setFormData({
                id: category.id,
                name: category.name,
                parentId: category.parentId || "",
                iconFile: null,
                iconPreview: getImageFullUrl(category.icon) || "",
                level
            });
            setMode("edit");
        } else {
            // Adding
            setFormData({
                id: "",
                name: "",
                parentId: parent ? parent.id : "",
                iconFile: null,
                iconPreview: "",
                level: parent ? level + 1 : 0
            });
            setMode("add");
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => setDialogOpen(false);

    // ✅ Handle file select
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
                toast.error("Only JPG, PNG, or WEBP images are allowed!");
                return;
            }
            setFormData({
                ...formData,
                iconFile: file,
                iconPreview: URL.createObjectURL(file)
            });
        }
    };

    // ✅ Save category (create/update)
    const handleSaveCategory = async () => {
        if (!formData.name) {
            toast.error("Category name is required");
            return
        }
        setLoading(true)
        try {
            const data = new FormData();
            data.append("name", formData.name);
            if (formData.parentId) data.append("parentId", formData.parentId);
            if (formData.iconFile) data.append("icon", formData.iconFile);

            if (mode === "edit") {
                await apiClient.put(`/api/categories/${formData.id}`, data, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                toast.success("Category updated");
            } else if (mode === "add") {
                await apiClient.post("/api/categories", data, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                toast.success("Category created");
            }
            handleCloseDialog();
            fetchCategories();
        } catch (err) {
            toast.error("Failed to save category");
        }finally{
            setLoading(false)
        } 
    };

    // ✅ Recursive render
    const renderCategory = (category, level = 0) => (
        <Accordion key={category.id} sx={{ border: 'solid 2px', marginBottom:'10px' }}>
            <AccordionSummary
                expandIcon={level === 2 ? null : <ExpandMoreIcon />}
                aria-controls={`panel-${category.id}`}
                id={`panel-header-${category.id}`}
            >
                <ListItem
                    sx={{ pl: level * 3 }}
                    secondaryAction={
                        <>
                            {/* Edit Button (Super category cannot change parent) */}
                            <IconButton
                                edge="end"
                                onClick={() => handleOpenDialog(category, null, level)}
                            >
                                <Edit color="primary" />
                            </IconButton>

                            {/* Delete */}
                            <IconButton edge="end" onClick={() => requestDelete(category)}>
                                <Delete color="error" />
                            </IconButton>

                            {/* Add (Disable on third level) */}
                            {level != 2 &&
                                <IconButton
                                    edge="end"
                                    onClick={() => handleOpenDialog(null, category, level)}
                                    disabled={level === 2}
                                >
                                    <Add color={level === 2 ? "disabled" : "success"} />
                                </IconButton>
                            }
                        </>
                    }
                >
                    <ListItemAvatar>
                        <Avatar src={getImageFullUrl(category.icon)} alt={category.name}>
                            <CategoryIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={category.name} />
                </ListItem>
            </AccordionSummary>
            {level != 2 &&
                <AccordionDetails>
                    {category.subcategories && category.subcategories.length > 0 ? (
                        <List component="div" disablePadding>
                            {category.subcategories.map((sub) =>
                                renderCategory(sub, level + 1)
                            )}
                        </List>
                    ) : (
                        <Typography>No Sub categories</Typography>
                    )}
                </AccordionDetails>
            }
        </Accordion>
    );

    useEffect(() => {
        fetchCategories();
    }, []);

    return (
        <Box>

            <Card >
                <CardContent>
                    <Typography variant="h5" gutterBottom>
                        Category Management
                    </Typography>

                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                    >
                        Add Super Category
                    </Button>
                    <List>
                        {categories.length > 0 ? (
                            categories?.map((cat) => renderCategory(cat))
                        ) : (
                            <Typography>No categories found</Typography>
                        )}
                    </List>
                </CardContent>
            </Card>

            {/* ✅ Add/Edit Category Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth>
                <DialogTitle>
                    {mode === "edit" ? "Edit Category" : "Add Category"}
                </DialogTitle>
                <DialogContent dividers
                    sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1, paddingTop:'10px',}}
                >
                    <TextField
                        label="Category Name"
                        value={formData.name}
                        onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                        }
                        fullWidth
                    />

                    {/* Parent Selection Rules */}
                    {mode !== "add" && formData.level !== 0 && (
                        <TextField
                            select
                            label="Parent Category"
                            value={formData.parentId}
                            onChange={(e) =>
                                setFormData({ ...formData, parentId: e.target.value })
                            }
                            fullWidth
                        >
                            {formData.level === 1 &&
                                categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </MenuItem>
                                ))}
                            {formData.level === 2 &&
                                categories.flatMap((cat) =>
                                    cat.subcategories.map((sub) => (
                                        <MenuItem key={sub.id} value={sub.id}>
                                            {cat.name} → {sub.name}
                                        </MenuItem>
                                    ))
                                )}
                        </TextField>
                    )}

                    {/* ✅ File Upload with Preview */}
                    <Button variant="outlined" component="label">
                        Upload Icon
                        <input hidden type="file" accept="image/*" onChange={handleFileChange} />
                    </Button>
                    {formData.iconPreview && (
                        <Box sx={{ mt: 1 }}>
                            <FallbackImage
                                src={formData.iconPreview}
                                alt="preview"
                                style={{ maxWidth: "100px", borderRadius: "8px" }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button disabled={loading} variant="contained" onClick={handleSaveCategory}>
                        {mode === "edit" ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ✅ Confirmation dialog */}
            <ConfirmDialog
                open={confirmOpen}
                title={`Delete Category - ${deleteData?.name}`}
                content="Are you sure you want to delete this category?"
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => {
                    deleteCategory(deleteData?.id)
                    setConfirmOpen(false)
                }}
            />
        </Box>
    );
};

export default CategoryManager;
